import apiClient from './client';
import { config } from '../config';

const NETWORK_ERROR_RESPONSE = {
  success: false,
  error: 'NETWORK_ERROR',
  message: 'Errore di connessione',
};

const getApiOrigin = () => {
  const baseUrl = config.API_BASE_URL || '/api';
  const originFallback = typeof window !== 'undefined' ? window.location.origin : '';

  try {
    const resolved = new URL(baseUrl, originFallback || 'http://localhost');
    return resolved.origin;
  } catch (_) {
    return originFallback;
  }
};

export const resolvePartnerImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return imageUrl;
  const trimmed = imageUrl.trim();
  if (!trimmed) return '';

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }

  if (trimmed.startsWith('/api/')) {
    const apiOrigin = getApiOrigin();
    return apiOrigin ? `${apiOrigin}${trimmed}` : trimmed;
  }

  return trimmed;
};

/**
 * Fetch partners shown on the public collaborations page.
 */
export const getCollaborationPartners = async () => {
  try {
    const response = await apiClient.get('/partners/collaborations');
    return response.data;
  } catch (error) {
    console.error('[Partners] Failed to fetch collaboration partners:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      ...NETWORK_ERROR_RESPONSE,
      partners: [],
    };
  }
};

/**
 * Fetch partner campaign data for /benvenuti/:partner_slug.
 * Returns null when the campaign is not found (404) or request fails.
 */
export const getPartnerCampaign = async (slug) => {
  try {
    const response = await apiClient.get(`/partners/campaigns/${encodeURIComponent(slug)}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('[Partners] Failed to fetch partner campaign:', error);
    return null;
  }
};

/**
 * Admin: list partners with optional filters.
 */
export const getAdminPartners = async (options = {}, token) => {
  try {
    const params = new URLSearchParams();
    if (options.query) params.append('query', options.query);
    if (options.limit != null) params.append('limit', String(options.limit));
    if (options.offset != null) params.append('offset', String(options.offset));

    const headers = { Authorization: `Bearer ${token}` };
    const queryString = params.toString();
    const endpoint = queryString ? `/partners?${queryString}` : '/partners';
    const response = await apiClient.get(endpoint, { headers });
    return response.data;
  } catch (error) {
    console.error('[Partners] Failed to fetch admin partners list:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      ...NETWORK_ERROR_RESPONSE,
      partners: [],
      total: 0,
    };
  }
};

/**
 * Admin: create a new partner.
 */
export const createPartner = async (payload, token) => {
  try {
    const headers = { Authorization: `Bearer ${token}` };
    const response = await apiClient.post('/partners', payload, { headers });
    return response.data;
  } catch (error) {
    console.error('[Partners] Failed to create partner:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return NETWORK_ERROR_RESPONSE;
  }
};

/**
 * Admin: update an existing partner.
 */
export const updatePartner = async (partnerId, payload, token) => {
  try {
    const headers = { Authorization: `Bearer ${token}` };
    const response = await apiClient.patch(`/partners/${partnerId}`, payload, { headers });
    return response.data;
  } catch (error) {
    console.error('[Partners] Failed to update partner:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return NETWORK_ERROR_RESPONSE;
  }
};

/**
 * Admin: upload a partner image file.
 */
export const uploadPartnerImage = async (partnerId, file, token) => {
  try {
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    };
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/partners/${partnerId}/image`, formData, { headers });
    return response.data;
  } catch (error) {
    console.error('[Partners] Failed to upload partner image:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return NETWORK_ERROR_RESPONSE;
  }
};
